import { useState } from "react";
import { Send, X, Loader2, CheckCircle2, Mail } from "lucide-react";
import api from "../lib/api";

/** EmailPackModal — shared by /badges (self-serve) and /squad/:id (leader on-behalf-of).
 *  `endpoint` is the full API path (without /api prefix). */
export default function EmailPackModal({ open, onClose, endpoint, subjectName }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSending(true);
    try {
      await api.post(endpoint, { to: to.trim().toLowerCase(), message: message.trim() || undefined });
      setSent(true);
      setTimeout(() => {
        setSent(false); setTo(""); setMessage("");
        onClose();
      }, 2200);
    } catch (e2) {
      const data = e2?.response?.data;
      const detail = (data && typeof data === "object") ? data.detail : null;
      setErr(detail || "Email send failed. Try again or check the recipient address.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="email-pack-modal">
      <div className="w-full max-w-lg flat-card p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between bg-[var(--bg)]">
          <div className="flex items-center gap-2">
            <Mail size={16} color="var(--cyan)" strokeWidth={1.5} />
            <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">// EMAIL_COMPLIANCE_PACK</div>
          </div>
          <button onClick={onClose} className="text-[var(--text-2)] hover:text-white" data-testid="email-modal-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          {sent ? (
            <div className="text-center py-6" data-testid="email-pack-sent">
              <CheckCircle2 size={32} className="mx-auto mb-3" color="var(--green)" strokeWidth={1.5} />
              <div className="font-display font-bold uppercase text-lg text-[var(--green)]">Transmission Sent</div>
              <p className="text-sm text-[var(--text-2)] mt-2">
                The compliance pack PDF has been emailed to <span className="text-[var(--cyan)]">{to}</span>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <div className="overline mb-1">// SUBJECT</div>
                <h2 className="font-display font-bold uppercase text-lg text-white">{subjectName}</h2>
                <p className="text-xs text-[var(--text-2)] mt-1">
                  Sends the full compliance pack PDF as an attachment, with a tactical-themed cover email.
                </p>
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">Recipient email</label>
                <input
                  type="email" required value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="input-tac mt-1"
                  placeholder="procurement@contractor.com.au"
                  data-testid="email-pack-to"
                  autoFocus
                />
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">
                  Note (optional)
                </label>
                <textarea
                  rows={3} value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="input-tac mt-1 resize-none"
                  maxLength={2000}
                  placeholder="Hi Sarah — attached training evidence for the ADF tender response. Happy to walk through it on a call."
                  data-testid="email-pack-message"
                />
              </div>

              {err && (
                <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2" data-testid="email-pack-error">
                  {err}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border)]">
                <button type="button" onClick={onClose} className="btn-ghost text-xs px-3 py-1.5" data-testid="email-pack-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={sending || !to.includes("@")} className="btn-primary inline-flex items-center gap-2 text-xs px-3 py-1.5" data-testid="email-pack-send">
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  {sending ? "Sending…" : "Send PDF"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
